import type {LanguageCode, NamespaceId} from 'components/translation/utils';
import {ONE_MINUTE_MS} from 'contexts/TimeContext';
import {getDoc} from 'firebase/firestore';
import {useEffect, useMemo, useRef, useState} from 'react';
import type {CourseId} from 'types/common';
import {useUserRole} from 'contexts/ProfileContext';
import {Role} from 'types/role';
import {isMinimumRole} from 'contexts/profileUtil';
import {getCourseTranslationDocument} from '../../firebase/models/translationDocumentReferences';
import {useCipHttpsCallable} from '../../firebase/functions/use_cip_https_callable';
import type {
  TranslateAllMessagesRequest,
  TranslateAllMessagesResponse,
} from '../../firebase/functions/translateAllMessagesApi';

/**
 * The key in `localStorage` that stores the unknown translation keys seen on
 * the current page.
 */
const UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY = 'cip__unknownTranslationKeys';

/**
 * The interval at which to automatically upload unknown translation keys.
 */
const AUTOMATIC_UPLOAD_INTERVAL = 5 * ONE_MINUTE_MS;

const useTranslateAllMessages = () =>
  useCipHttpsCallable<
    TranslateAllMessagesRequest,
    TranslateAllMessagesResponse
  >('translateallmessages');

/**
 * The result of the `useTranslationUploader` hook.
 */
interface UseTranslationUploaderResult {
  /** True if the translations are currently being uploaded. */
  translationsUploading: boolean;
  /** True if any translations have ever been uploaded, false otherwise. */
  hasUploadedTranslations: boolean;
  /** Marks a translation key as needing upload to the backend.
   * @param courseId - The course ID to mark the key for.
   * @param namespace - The namespace to mark the key for.
   * @param key - The key to mark.
   */
  addUnknownTranslationKeyToUploadCache: (
    courseId: CourseId,
    namespace: NamespaceId,
    key: string,
  ) => void;
  /** Uploads the unknown translation keys to the backend. */
  uploadUnknownTranslationKeys: ({
    onAllTranslationsUploaded,
  }: {
    /** A callback for when all translations are uploaded. */
    onAllTranslationsUploaded?: () => void;
  }) => Promise<void>;
  /** Clears the local cache (i.e. `localStorage`) of unknown translation keys.
   */
  clearLocalUnknownTranslationKeys: () => void;
  /** Uploads translations to the backend.
   * @param keysToTranslate - A map from course ID to a map from namespace to
   * a list of keys to translate.
   * @param targetLanguages - The languages to translate the keys to.
   * @param onAllTranslationsUploaded - A callback for when all translations
   * are uploaded.
   */
  writeTranslationsToFirebase: ({
    keysToTranslate,
    targetLanguages,
    onAllTranslationsUploaded,
  }: {
    keysToTranslate: Record<CourseId, Record<NamespaceId, string[]>>;
    targetLanguages: LanguageCode[];
    onAllTranslationsUploaded?: () => void;
  }) => Promise<void>;
  /**
   * Checks if there are any unknown translation keys stored in the local cache.
   */
  checkHasUnknownTranslationKeys: () => boolean;
}

/**
 * Manages the uploading of unknown translation keys to the backend. Exposes
 * functions for adding keys to the unknown key cache and uploading arbitrary
 * keys (with translations) to the backend.
 *
 * @description Whenever our `t` translate function is called, we store any keys
 * not available from the loaded translation data in a local unknown translation
 * key cache. This cache is stored in `localStorage` as a map from course ID to
 * a map from namespace to a map from key to a boolean indicating whether the
 * key has been uploaded. `localStorage` is used as an "escape hatch" from
 * React's state management system, because `t` cannot produce stateful changes
 * to our unknown key cache on render as it is not a hook. Separately, we have
 * and updates their state so they aren't uploaded twice. Since unknown keys are
 * only added when not present in the cache and the uploader only mutates keys
 * that are present in the cache without removing them, we do not need to worry
 * about races between the `t` function and the uploader.
 * @returns {@inheritdoc UseTranslationUploaderResult}
 */
export function useTranslationUploader(): UseTranslationUploaderResult {
  const [translateAllMessages, , translateAllMessagesError] =
    useTranslateAllMessages();
  const [translationsUploading, setTranslationsUploading] = useState(false);
  const [hasUploadedTranslations, setHasUploadedTranslations] = useState(false);
  // eslint-disable-next-line @eslint-react/naming-convention/ref-name -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const automaticUploadInterval = useRef<NodeJS.Timeout | null>(null);

  const {role, isLoading: isUserRoleLoading} = useUserRole();
  const hasPermissionToUploadTranslations = useMemo(() => {
    return !isUserRoleLoading && isMinimumRole(role, Role.STUDENT);
  }, [isUserRoleLoading, role]);

  useEffect(() => {
    if (!hasPermissionToUploadTranslations) {
      return;
    }
    automaticUploadInterval.current = setInterval(
      () => uploadUnknownTranslationKeys({onAllTranslationsUploaded: () => {}}),
      AUTOMATIC_UPLOAD_INTERVAL,
    );
    return () => {
      if (automaticUploadInterval.current) {
        clearInterval(automaticUploadInterval.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  useEffect(() => {
    if (!translateAllMessagesError) {
      return;
    }
    console.error(
      'Error translating messages via firebase function:',
      translateAllMessagesError,
    );
  }, [translateAllMessagesError]);

  async function writeTranslationsToFirebase({
    keysToTranslate,
    targetLanguages,
    onAllTranslationsUploaded = () => {},
  }: {
    keysToTranslate: Record<CourseId, Record<NamespaceId, string[]>>;
    targetLanguages: LanguageCode[];
    onAllTranslationsUploaded?: () => void;
  }): Promise<void> {
    if (
      Object.keys(keysToTranslate).length === 0 ||
      translationsUploading ||
      !hasPermissionToUploadTranslations
    ) {
      return;
    }
    setHasUploadedTranslations(true);
    for (const [courseId, namespaces] of Object.entries(keysToTranslate)) {
      translateAllMessages({
        courseId,
        // Skip the default language.
        targetLanguages: targetLanguages.filter(language => language !== 'en'),
        translations: namespaces,
      });
    }
    onAllTranslationsUploaded?.();
  }

  // Adds a translation key to the cache of unknown translation keys to upload.
  // Here our "upload cache" is a map from course ID to a map from namespace to
  // a map from key to whether the key has already been uploaded.
  const addUnknownTranslationKeyToUploadCache = (
    courseId: CourseId,
    namespace: NamespaceId,
    key: string,
  ): void => {
    if (
      !window.localStorage.getItem(UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY)
    ) {
      window.localStorage.setItem(
        UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY,
        JSON.stringify({}),
      );
    }
    const unknownTranslationKeys: Record<
      CourseId,
      Record<NamespaceId, Record<string, boolean>>
    > = JSON.parse(
      window.localStorage.getItem(UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY),
    );
    if (!unknownTranslationKeys[courseId]) {
      unknownTranslationKeys[courseId] = {};
    }
    if (!unknownTranslationKeys[courseId][namespace]) {
      unknownTranslationKeys[courseId][namespace] = {};
    }
    if (key in unknownTranslationKeys[courseId][namespace]) {
      return;
    }
    unknownTranslationKeys[courseId][namespace][key] = false;
    window.localStorage.setItem(
      UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY,
      JSON.stringify(unknownTranslationKeys),
    );
  };

  // Uploads unknown translation keys to the backend. If there are no unknown
  // translation keys to upload, this function does nothing.
  async function uploadUnknownTranslationKeys({
    onAllTranslationsUploaded = () => {},
  }: {
    onAllTranslationsUploaded?: () => void;
  }): Promise<void> {
    if (!hasPermissionToUploadTranslations) {
      return;
    }
    const unknownTranslationKeys: Record<
      CourseId,
      Record<NamespaceId, Record<string, boolean>>
    > = JSON.parse(
      window.localStorage.getItem(UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY) ??
        '{}',
    );
    if (Object.keys(unknownTranslationKeys).length === 0) {
      onAllTranslationsUploaded?.();
      return;
    }
    const keysToUpload: Record<CourseId, Record<NamespaceId, string[]>> = {};
    setTranslationsUploading(true);
    for (const [courseId, namespaces] of Object.entries(
      unknownTranslationKeys,
    )) {
      for (const [namespace, keys] of Object.entries(namespaces)) {
        const keysToUploadForNamespace = Object.entries(keys)
          .filter(([, isUploaded]) => !isUploaded)
          .map(([key]) => key);
        if (keysToUploadForNamespace.length === 0) {
          continue;
        }
        keysToUpload[courseId] = keysToUpload[courseId] ?? {};
        keysToUpload[courseId][namespace] =
          keysToUpload[courseId][namespace] ?? [];
        keysToUpload[courseId][namespace] = [
          ...keysToUpload[courseId][namespace],
          ...keysToUploadForNamespace,
        ];
      }
      const supportedLanguages = (
        await getDoc(getCourseTranslationDocument({courseId}))
      ).data()?.supportedLanguages ?? ['en'];
      await writeTranslationsToFirebase({
        keysToTranslate: keysToUpload,
        targetLanguages: supportedLanguages,
        onAllTranslationsUploaded: () => {
          for (const [courseId, namespaces] of Object.entries(keysToUpload)) {
            for (const [namespace, keys] of Object.entries(namespaces)) {
              for (const key of keys) {
                unknownTranslationKeys[courseId][namespace][key] = true;
              }
            }
          }
          window.localStorage.setItem(
            UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY,
            JSON.stringify(unknownTranslationKeys),
          );
          onAllTranslationsUploaded?.();
        },
      });
    }
    setTranslationsUploading(false);
  }

  function clearLocalUnknownTranslationKeys() {
    if (!hasPermissionToUploadTranslations) {
      return;
    }
    window.localStorage.removeItem(UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY);
  }

  function checkHasUnknownTranslationKeys() {
    const unknownTranslationKeys: Record<
      CourseId,
      Record<NamespaceId, Record<string, boolean>>
    > = JSON.parse(
      window.localStorage.getItem(UNKNOWN_TRANSLATION_KEY_LOCAL_STORAGE_KEY) ??
        '{}',
    );
    for (const namespaces of Object.values(unknownTranslationKeys)) {
      for (const keys of Object.values(namespaces)) {
        // If any key is mapped to false (i.e. not uploaded), then there are
        // unknown translation keys to upload.
        if (Object.values(keys).some(value => !value)) {
          return true;
        }
      }
    }
    return false;
  }

  return {
    addUnknownTranslationKeyToUploadCache,
    uploadUnknownTranslationKeys,
    clearLocalUnknownTranslationKeys,
    translationsUploading,
    hasUploadedTranslations,
    writeTranslationsToFirebase,
    checkHasUnknownTranslationKeys,
  };
}
